/**
 * The keyboard equivalent of a drag: Space picks up, arrows move, Space drops, Escape cancels.
 *
 * View ADR §12 is the requirement, and it is the one the mobile app fails outright — its reorder is
 * a long-press gesture with no alternative, which makes it unreachable to a screen-reader user.
 *
 * ── Nothing is written until the drop, and that is the whole design ──────────────────────────
 * A lift holds a PENDING position in component state. Arrows move it and announce; Space commits it
 * once; Escape drops it on the floor. So a cancelled keyboard move — like a cancelled pointer drag —
 * reaches no store operation at all, records no patch, pushes no undo entry and does not mark the
 * plan unsaved. Moving through four positions and pressing Escape leaves a byte-identical tree.
 *
 * The alternative — committing each arrow and undoing on Escape — was declined because it pushes
 * four undo entries for one decision the trainer then abandoned, and because a plan that reads as
 * unsaved after Escape is a plan the trainer has to think about.
 *
 * ── One axis, and optionally a stride on the other ──────────────────────────────────────────
 * A list has one axis and the perpendicular arrows mean nothing. A DATE GRID has two, and they mean
 * different distances on the same sequence of positions: left and right move by a day, up and down
 * by a week. `crossStride` is that number — absent for a list, 7 for the calendar — and it is a
 * stride over the SAME position list rather than a second axis, because a calendar's positions are
 * one run of days that happens to be drawn seven to a row. Without it, moving a session three weeks
 * out is twenty-one presses.
 *
 * ── Positions, not gaps ─────────────────────────────────────────────────────────────────────
 * This hook counts the POSITIONS an item can occupy — seven weekday columns, or five main-work
 * blocks — because that is what an announcement says ("pozycja 3 z 5") and what an arrow press
 * means. A pointer drop reports a GAP between siblings, and the two differ by one below the item's
 * own place; converting is the caller's line, and it is one line, made in the same place the
 * operation is dispatched from.
 *
 * ── Enter is not a pickup ───────────────────────────────────────────────────────────────────
 * The ADR offers "Space or Enter". Space alone is used here: the grip is a `button`, so Enter is
 * already its activation key and browsers fire a `click` for it — binding a lift to Enter would make
 * one press both open the lift and fire whatever else the button does. Space is the unambiguous one,
 * and it is what every published keyboard-drag pattern uses. Enter still WORKS while lifted, as the
 * confirm, because there is no competing meaning once the lift is up.
 *
 * ── A lift ENDS when the grip stops being where the keys go ──────────────────────────────────
 * Escape is not the only exit, and treating it as one wedged the canvas: Tab out of a lifted grip
 * and the lift stayed up forever — the tile kept the accent fill, the grip kept `data-lifted`, the
 * live region kept reporting a move in progress, and Escape pressed anywhere else did nothing,
 * because the only handler that reads it is the grip's. So a BLUR ends the lift, and so does the
 * host unmounting (a phase change, a mode flip, a dock swallowing the canvas).
 *
 * Both go through `onCancel`, so they take the exact path Escape takes: nothing is committed,
 * nothing reaches the store, and the trainer is TOLD the lift was dropped rather than left to
 * notice. A silent cancel would be the same defect one layer down.
 */

import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';

export type MoveAxis = 'horizontal' | 'vertical';

export interface KeyboardMove {
  readonly isLifted: boolean;
  /** Where the item is currently pointed, or `null` when nothing is lifted. */
  readonly target: number | null;
  readonly onKeyDown: (event: KeyboardEvent) => void;
  /**
   * Bind to the grip. A lift the trainer has tabbed away from is a lift they can no longer steer,
   * so it is dropped and announced — see the header.
   */
  readonly onBlur: () => void;
  /**
   * Drop the lift without committing, and say so. A no-op when nothing is lifted, so a caller can
   * fire it from a cleanup without checking first.
   */
  readonly cancel: () => void;
  /**
   * Pick the item up without a Space press — the menu's "Przestaw" entry, on a surface that draws no
   * grip (owner, 15 September 2026). The same guards and the same `onLift` as Space.
   */
  readonly lift: () => void;
  /**
   * The key and blur handlers for the ITEM itself, where there is no grip to bind them to.
   *
   * Both answer only for events aimed at the element they are bound to. The item holds controls of
   * its own, and their keys and blurs bubble through it: a Space on the fold button must fold, not
   * lift, and a menu item losing focus through a React portal must not cancel a lift it just started.
   */
  readonly host: {
    readonly onKeyDown: (event: KeyboardEvent) => void;
    readonly onBlur: (event: FocusEvent) => void;
  };
}

export function useKeyboardMove(options: {
  /** How many positions exist. A lift is refused when there is nowhere to go. */
  readonly count: number;
  /** Where the item sits now. */
  readonly index: number;
  readonly axis: MoveAxis;
  /**
   * How far the PERPENDICULAR arrows move, in positions. Omit for a list, where they mean nothing
   * and are left to the browser.
   */
  readonly crossStride?: number;
  readonly canLift: boolean;
  /**
   * Called ONCE, on pickup. Separate from `onPreview` because the two say different things: a
   * pickup has to name what was picked up and how the arrows now behave, and repeating that on
   * every arrow press would bury the position under the instruction.
   */
  readonly onLift: (target: number) => void;
  /** Called on every arrow, with the position now pointed at. */
  readonly onPreview: (target: number) => void;
  readonly onCommit: (target: number) => void;
  readonly onCancel: () => void;
}): KeyboardMove {
  const [target, setTarget] = useState<number | null>(null);
  const { count, index, axis, crossStride, canLift, onLift, onPreview, onCommit, onCancel } =
    options;

  const back = axis === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
  const forward = axis === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
  const crossBack = axis === 'horizontal' ? 'ArrowUp' : 'ArrowLeft';
  const crossForward = axis === 'horizontal' ? 'ArrowDown' : 'ArrowRight';

  /**
   * Whether a lift is up, readable SYNCHRONOUSLY and outside a render's closure.
   *
   * `target` cannot answer for the unmount cleanup, and the reason is worth stating because it cost
   * a working commit. A commit does three things in one batch — clear the lift, dispatch the move,
   * re-render — and the re-render moves the card to a different column, which UNMOUNTS this
   * instance. The cleanup then runs holding the closure from the last render that completed, where
   * `target` was still the position the trainer had chosen — so it cancelled a move that had
   * already succeeded and replaced "Przeniesiono Push A na wtorek" with "Anulowano przenoszenie".
   * The ref is written in the handler, before React has re-rendered anything, so by the time any
   * cleanup runs it already says the lift is over.
   */
  const isLifted = useRef(false);

  /** Release the pending position without saying anything — the commit path's own tidy-up. */
  const release = () => {
    isLifted.current = false;
    setTarget(null);
  };

  /**
   * Release it AND report it. The one exit every cancellation takes: Escape, a blur, an unmount.
   *
   * Guarded rather than leaving the caller to check, because two of the three callers are lifecycle
   * events that fire whether or not anything was lifted.
   */
  const cancel = () => {
    if (!isLifted.current) return;
    release();
    onCancel();
  };

  // The latest `cancel`, so the unmount cleanup below can be registered ONCE. Registering it with
  // `cancel` in the dependency array would tear it down and rebuild it on every render, and a
  // cleanup that runs on a re-render would cancel a lift the trainer is still holding. Written in
  // an EFFECT rather than during render (`react-hooks/refs`), which is safe because its only reader
  // is an unmount cleanup — long after effects have run.
  const latestCancel = useRef(cancel);
  useEffect(() => {
    latestCancel.current = cancel;
  });
  useEffect(
    () => () => {
      latestCancel.current();
    },
    [],
  );

  const lift = () => {
    if (isLifted.current || !canLift || count <= 1) return;
    isLifted.current = true;
    setTarget(index);
    onLift(index);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (target === null) {
      if (event.key !== ' ' || !canLift || count <= 1) return;
      // The page scrolls on Space, and a grid that scrolls out from under a lift is a lift the
      // trainer immediately loses sight of.
      event.preventDefault();
      lift();
      return;
    }

    if (event.key === 'Escape') {
      // Absorbed: the dock and the builder both listen for Escape, and a cancelled lift must not
      // also close the surface the trainer is standing in.
      event.preventDefault();
      event.stopPropagation();
      cancel();
      return;
    }

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      release();
      onCommit(target);
      return;
    }

    const stride = crossStride ?? 0;
    const step =
      event.key === back
        ? -1
        : event.key === forward
          ? 1
          : stride === 0
            ? 0
            : event.key === crossBack
              ? -stride
              : event.key === crossForward
                ? stride
                : 0;
    if (step === 0) return;
    event.preventDefault();
    // Clamped rather than wrapped. Wrapping turns "one too far" into a jump to the other end of the
    // week, which is a move the trainer did not ask for and has to notice to undo.
    const next = Math.min(Math.max(target + step, 0), count - 1);
    if (next === target) return;
    setTarget(next);
    onPreview(next);
  };

  const host = {
    onKeyDown: (event: KeyboardEvent) => {
      if (event.target === event.currentTarget) onKeyDown(event);
    },
    onBlur: (event: FocusEvent) => {
      if (event.target === event.currentTarget) cancel();
    },
  };

  return { isLifted: target !== null, target, onKeyDown, onBlur: cancel, cancel, lift, host };
}
